import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { useDropzone } from 'react-dropzone';
import {
  Select,
  MenuItem,
  Button,
  InputLabel,
  OutlinedInput,
  LinearProgress
} from '@material-ui/core';
import TrashIcon from '@material-ui/icons/DeleteOutline';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import Swal from 'sweetalert2';
import { TextField } from '@mui/material';
import { apiOptions, apiPostForm } from '../../../../functions/api'
import axios from 'axios'
import { URL_DEV, URL_ASSETS } from '../../../../constants'
import { Box, Grid } from '@mui/material'; // al inicio del archivo

/* ===================== CONSTANTES ===================== */

const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const normalize = (text) =>
  String(text || '')
    .trim()
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')  // elimina signos de puntuación comunes
    .replace(/\s+/g, ' ')                         // colapsa espacios múltiples
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');             // elimina acentos



const calcularImporte = (precioUnitario, cantidad) => {
  const pu = parseFloat(precioUnitario) || 0;
  const qty = parseFloat(cantidad) || 0;
  return parseFloat((pu * qty).toFixed(2));
};

/* ===================== FAST INPUT ===================== */

const FastInput = React.memo(({ value, onCommit }) => {
  const [local, setLocal] = useState(String(value));

  useEffect(() => {
    setLocal(String(value));
  }, [value]);

  const handleChange = (e) => setLocal(e.target.value);

  const commitChange = () => {
    const numeric = parseFloat(local);
    if (!isNaN(numeric)) {
      onCommit(numeric);
    }
  };

  const handleKeyDown = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
    if (e.key === 'Enter') {
      commitChange();
      e.target.blur();
    }
  };

  return (
    <OutlinedInput
      type="number"
      value={local}
      onChange={handleChange}
      onBlur={commitChange}
      onKeyDown={handleKeyDown}
      inputProps={{
        min: 0,
        step: 0.01,
        inputMode: 'decimal',
        style: { textAlign: 'right' }
      }}
      sx={{
        width: '80px',
        fontSize: '14px',
        '& input': { padding: '8px' }
      }}
      size="small"
    />
  );
});

const PresupuestoNombre = React.memo(({ value, onChange }) => {
  return (
    <TextField
      label="NOMBRE DEL PRESUPUESTO"
      id="comment"
      value={value}
      onChange={onChange}
      fullWidth
      InputLabelProps={{ shrink: true }}
    />
  );
});

/* ===================== COMPONENTE PRINCIPAL ===================== */

export default function App(props) {
  const { reload, handleClose } = props

  const departamentos = useSelector(state => state.opciones.areas);
  const empresas = useSelector(state => state.opciones.empresas);
  const auth = useSelector(state => state.authUser);
  // const usuario = useSelector(state => state.auth.user);
  const usuario = useSelector(state => state.authUser.departamento.departamentos[0])

  const [tables, setTables] = useState([]);
  const [tableName, setTableName] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [comment, setComment] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [erroresExcel, setErroresExcel] = useState([]);


  const handleAddTable = () => {
    if (!tableName || tables.some(t => t.name === tableName)) return;
    setTables([...tables, { id: Date.now(), name: tableName, rows: [] }]);
    setTableName('');
  };

  const handleDeleteTable = (tableId) => {
    setTables(tables.filter(t => t.id !== tableId));
  };

   const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleAddRow = (tableId) => {
    setTables(prev =>
      prev.map(table =>
        table.id === tableId
          ? {
              ...table,
              rows: [
                ...table.rows,
                {
                  id: Date.now(),
                  select1: '',
                  select2: '',
                  montos: months.map(() => ({
                    precio_unitario: 0,
                    cantidad: 0,
                    importe: 0
                  }))
                }
              ]
            }
          : table
      )
    );
  };

  const handleDeleteRow = (tableId, rowId) => {
    setTables(prev =>
      prev.map(table =>
        table.id === tableId
          ? { ...table, rows: table.rows.filter(r => r.id !== rowId) }
          : table
      )
    );
  };

  const handleSelectChange = (tableId, rowId, field, value) => {
    setTables(prev =>
      prev.map(table =>
        table.id !== tableId
          ? table
          : {
              ...table,
              rows: table.rows.map(row =>
                row.id === rowId ? { ...row, [field]: value } : row
              )
            }
      )
    );
  };

  const updateMonto = useCallback((tableId, rowId, monthIndex, field, value) => {
  setTables(prev =>
    prev.map(table =>
      table.id !== tableId
        ? table
        : {
            ...table,
            rows: table.rows.map(row => {
              if (row.id !== rowId) return row;
              const montos = [...row.montos];
              montos[monthIndex][field] = value;

              // Recalcular importe solo si cambió precio o cantidad
              if (['precio_unitario', 'cantidad'].includes(field)) {
                montos[monthIndex].importe = calcularImporte(
                  montos[monthIndex].precio_unitario,
                  montos[monthIndex].cantidad
                );
              }

              return { ...row, montos };
            })
          }
    )
  );
}, []);

  const totalDepartamento = (table) =>
    parseFloat(
      table.rows.reduce(
        (a, r) => a + r.montos.reduce((m, v) => m + v.importe, 0),
        0
      ).toFixed(2)
    );

  const totalGlobal = useMemo(() =>
    parseFloat(
      tables.reduce((a, t) => a + totalDepartamento(t), 0).toFixed(2)
    )
  , [tables]);

  const handleExcelUpload = (file) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(10);
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        setUploadProgress(30);
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        setUploadProgress(50);

        const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        const tablesMap = {};
        const errores = [];

        raw.slice(1).forEach((row, index) => {
          const deptName = row[0];
          const partName = row[1];
          const subName = row[2];
          const filaExcel = index + 2;

          if (!deptName || !partName || !subName) {
            errores.push({ filaExcel, motivo: 'Faltan columnas base' });
            return;
          }

          // Normalizamos todo desde el inicio
          const deptNameNorm = normalize(deptName);
          const partNameNorm = normalize(partName);
          const subNameNorm = normalize(subName);

          const dept = departamentos.find(d => normalize(d.nombreArea) === deptNameNorm);
          if (!dept) {
            errores.push({ filaExcel, motivo: `Departamento no existe: ${deptName}` });
            return;
          }

          const partida = dept.partidas.find(p => normalize(p.nombre) === partNameNorm);
          if (!partida) {
            errores.push({ filaExcel, motivo: `Partida no existe: ${partName}` });
            return;
          }

          const subpartidasNorm = (partida.subpartidas || []).map(s => ({
            ...s,
            normalized: normalize(s.nombre)
          }));

          const sub = subpartidasNorm.find(s => s.normalized === subNameNorm);

          if (!sub) {
            errores.push({ filaExcel, motivo: `Subpartida no existe: ${subName}` });
            return;
          }

          if (!tablesMap[dept.id_area]) {
            tablesMap[dept.id_area] = {
              id: Date.now() + Math.random(),
              name: dept.id_area,
              rows: []
            };
          }

          let colIndex = 3;
          const montos = months.map(() => {
            const precio = parseFloat(String(row[colIndex]).replace(/,/g, '')) || 0;
            const cantidad = parseFloat(String(row[colIndex + 1]).replace(/,/g, '')) || 0;
            const importe = calcularImporte(precio, cantidad);
            colIndex += 3;
            return { precio_unitario: precio, cantidad, importe };
          });

          tablesMap[dept.id_area].rows.push({
            id: Date.now() + Math.random(),
            select1: partida.id,
            select2: sub.id,
            montos
          });
        });


        const newTables = Object.values(tablesMap);
        setTables(newTables);
        setUploadProgress(100);
        setErroresExcel(errores);

        Swal.fire({
          title: 'Importación completa',
          text: `Filas cargadas: ${newTables.reduce((a, t) => a + t.rows.length, 0)} | Errores: ${errores.length}`,
          icon: errores.length ? 'warning' : 'success',
          showCancelButton: errores.length > 0,
          confirmButtonText: 'Aceptar',
          cancelButtonText: 'Ver errores',
        }).then((result) => {
          if (result.dismiss === Swal.DismissReason.cancel && errores.length > 0) {
            const listaErrores = errores.map(e => `Fila ${e.filaExcel}: ${e.motivo}`).join('\n');
            Swal.fire({
              icon: 'error',
              title: 'Errores de importación',
              html: `<pre style="text-align: left; white-space: pre-wrap;">${listaErrores}</pre>`,
              confirmButtonText: 'Cerrar',
              width: 600,
            });
          }
        });

        setTimeout(() => {
          setUploading(false);
          setUploadProgress(0);
        }, 600);

      } catch (err) {
        console.error(err);
        setUploading(false);
        Swal.fire('Error', 'Error al procesar el Excel', 'error');
      }
    };

    reader.readAsArrayBuffer(file);
  };



  const { getRootProps, getInputProps } = useDropzone({
    accept: '.xlsx,.xls',
    multiple: false,
    onDrop: (files) => {
      if (files.length) handleExcelUpload(files[0]);
    }
  });

  const handleSendTables = async () => {
    try {
      if (!empresa || !startDate || !endDate || !comment) {
        return Swal.fire('Campos incompletos', 'Verifica empresa, fechas y comentario', 'warning');
      }

      const formattedData = [];
      let totalGlobal = 0;

      tables.forEach((table) => {
        const department = departamentos.find((item) => item.id_area === table.name);

        table.rows.forEach((row) => {
          const rowData = {
            departamentoId: department ? department.id_area : null,
            departamento: department ? department.nombreArea : '',
            partida: row.select1,
            subpartida: row.select2,
            cantidad: row.cantidad || 0,
            unidad: row.unidad || '',
            months: row.montos.map((monto, monthIndex) => ({
              month: months[monthIndex],
              precio_unitario: monto.precio_unitario,
              cantidad: monto.cantidad,
              unidad: monto.unidad,
              importe: monto.importe
            })),
          };

        const rowTotal = rowData.months.reduce((acc, m) => acc + m.importe, 0);
          totalGlobal += rowTotal;

          formattedData.push(rowData);
        });
      });

      const dataToSend = {
        total: totalGlobal,
        data: formattedData,
        fecha_inicio: startDate,
        fecha_fin: endDate,
        nombre: comment,
        tab: 'departamento',
        id_departamento: usuario.id,
        empresa: empresa
      };

      Swal.fire({ title: 'Guardando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

        // apiPostForm(`presupuestosdep?departamento_id=${usuario.id}`, dataToSend, auth)
        //   .then(res => {
        //     console.log(res)
        //       Swal.close()
        //       Swal.fire({
        //           icon: 'success',
        //           title: 'Presupuesto creado con éxito',
        //           timer: 2000
        //       }).then(() => {
        //           if (reload) {
        //               reload.reload()
        //           }
        //           handleClose()
        //       })

        //   })
        axios.post(`${URL_DEV}presupuestosdep?departamento_id=${usuario.id}`, dataToSend, {
        headers: {
          Authorization: `Bearer ${auth.access_token}`, // ✅ aquí corriges
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        Swal.close();
        Swal.fire({
          icon: 'success',
          title: 'Presupuesto creado con éxito',
          timer: 2000
        }).then(() => {
          if (reload) reload.reload();
          handleClose();
        });
      })
      .catch((error) => {
        console.error(error);
        Swal.fire('Error', 'No se pudo guardar el presupuesto', 'error');
      });


      // if (!response.ok) throw new Error('Error al guardar');

      Swal.close();
      Swal.fire({ icon: 'success', title: 'Presupuesto guardado correctamente', timer: 2000 });
      setTables([]);

    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'No se pudo guardar el presupuesto', 'error');
    }
  };

 return (
  <Box sx={{ p: 2 }}>
    <Grid container spacing={2} alignItems="flex-end">
      <Grid item xs={12} md={3}>
        <InputLabel>Departamento</InputLabel>
        <Select
          value={tableName}
          onChange={(e) => setTableName(e.target.value)}
          fullWidth
        >
          {departamentos.map(d => (
            <MenuItem key={d.id_area} value={d.id_area}>{d.nombreArea}</MenuItem>
          ))}
        </Select>
        <Button onClick={handleAddTable} variant="outlined" sx={{ mt: 1 }}>
          Agregar
        </Button>
      </Grid>

      <Grid item xs={12} md={3}>
        <InputLabel>Empresa</InputLabel>
        <Select
          value={empresa}
          onChange={(e) => setEmpresa(e.target.value)}
          fullWidth
        >
          {empresas.map(emp => (
            <MenuItem key={emp.id} value={emp.id}>{emp.nombre}</MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={6} md={2}>
        <TextField
          label="Fecha inicio"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </Grid>

      <Grid item xs={6} md={2}>
        <TextField
          label="Fecha fin"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <PresupuestoNombre value={comment} onChange={handleCommentChange} />
      </Grid>

      <Grid item xs={12} md={3}>
        <Box>
          <strong>Total Global: </strong>
          {totalGlobal.toLocaleString('es-MX', {
            style: 'currency',
            currency: 'MXN'
          })}
        </Box>
      </Grid>

      <Grid item xs={12} md={4}>
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed #3f51b5',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            cursor: 'pointer'
          }}
        >
          <input {...getInputProps()} />
          <strong>Arrastra Excel aquí</strong>
        </Box>
      </Grid>

      <Grid item xs={12} md={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendTables}
          fullWidth
        >
          Guardar Presupuesto
        </Button>
      </Grid>
    </Grid>

    {uploading && (
      <LinearProgress
        value={uploadProgress}
        variant="determinate"
        sx={{ mt: 2 }}
      />
    )}

    {tables.map((table) => (
      <Box key={table.id} sx={{ mt: 4 }}>
        <Box display="flex" alignItems="center" mb={1}>
          <h3 style={{ marginRight: 8 }}>
            {departamentos.find(d => d.id_area === table.name)?.nombreArea}
          </h3>
          <TrashIcon
            onClick={() => handleDeleteTable(table.id)}
            style={{ cursor: 'pointer', color: 'red' }}
          />
        </Box>

        <Box mb={1}>
          <strong>Total:</strong>{' '}
          {totalDepartamento(table).toLocaleString('es-MX', {
            style: 'currency',
            currency: 'MXN'
          })}
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Partida</th>
                <th>Subpartida</th>
                {months.map((m) => (
                  <th key={m}>
                    {m}
                    <br />PU / Cant / Imp
                  </th>
                ))}
                <th>Total</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Select
                      value={row.select1}
                      onChange={(e) =>
                        handleSelectChange(table.id, row.id, 'select1', e.target.value)
                      }
                      fullWidth
                    >
                      {departamentos
                        .find((d) => d.id_area === table.name)
                        ?.partidas.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                        ))}
                    </Select>
                  </td>

                  <td>
                    <Select
                      value={row.select2}
                      onChange={(e) =>
                        handleSelectChange(table.id, row.id, 'select2', e.target.value)
                      }
                      fullWidth
                    >
                      {departamentos
                        .find((d) => d.id_area === table.name)
                        ?.partidas.find((p) => p.id === row.select1)
                        ?.subpartidas.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                        ))}
                    </Select>
                  </td>

                  {row.montos.map((m, i) => (
                    <td key={i}>
                      <FastInput
                        value={m.precio_unitario}
                        onCommit={(v) =>
                          updateMonto(table.id, row.id, i, 'precio_unitario', v)
                        }
                      />
                      <FastInput
                        value={m.cantidad}
                        onCommit={(v) =>
                          updateMonto(table.id, row.id, i, 'cantidad', v)
                        }
                      />
                      <div style={{ textAlign: 'right', fontWeight: 500 }}>
                        {m.importe.toLocaleString('es-MX', {
                          style: 'currency',
                          currency: 'MXN'
                        })}
                      </div>
                    </td>
                  ))}

                  <td>
                    {row.montos
                      .reduce((a, m) => a + m.importe, 0)
                      .toLocaleString('es-MX', {
                        style: 'currency',
                        currency: 'MXN'
                      })}
                  </td>

                  <td>
                    <TrashIcon
                      onClick={() => handleDeleteRow(table.id, row.id)}
                      style={{ cursor: 'pointer', color: 'red' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        <Box mt={1}>
          <PlaylistAddIcon
            onClick={() => handleAddRow(table.id)}
            style={{ cursor: 'pointer', color: 'green' }}
          />
        </Box>
      </Box>
    ))}
  </Box>
);

}
